/**
 * In-app purchases — the ONE `createOpenMarketsStore` for the app, on the shared auth instance
 * (lib/omauth.ts `getOmAuth()`; never a second auth). Not the SDK's `useOpenMarketsStore` hook:
 * that makes a store per component, and holdings belong in react-query next to everything else,
 * keyed by who's signed in so one account's purchases never show for another.
 *
 * The server is the authority on what a user holds: gate on `holdings`, never on a stored
 * `paid` result. Every purchase is confirmed by the user on an OpenMarkets page (popup, or a
 * redirect that comes back with `?om_order=`, handled by {@link handleStoreReturn} on load).
 */
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { createOpenMarketsStore, type PurchaseResult, type StoreHoldings, type StoreProducts } from "@openmarketsai/app-sdk";
import { getOmAuth, hasSdkSession, useAuthIdentity } from "./omauth.js";

let instance: ReturnType<typeof createOpenMarketsStore> | null = null;

export function getOmStore(): ReturnType<typeof createOpenMarketsStore> {
  if (!instance) instance = createOpenMarketsStore(getOmAuth());
  return instance;
}

/** What the signed-in user holds in greenbook (entitlements + units). Null signed out. */
export function useStoreHoldings() {
  const identity = useAuthIdentity();
  return useQuery<StoreHoldings | null>({
    queryKey: ["store", "holdings", identity],
    enabled: identity !== null && hasSdkSession(),
    queryFn: () => getOmStore().entitlements({ fresh: true }),
    staleTime: 30_000,
    retry: 1,
  });
}

/** greenbook's products for this user (live installs see approved products only). */
export function useStoreProducts(enabled = true) {
  const identity = useAuthIdentity();
  return useQuery<StoreProducts>({
    queryKey: ["store", "products", identity],
    enabled: enabled && identity !== null && hasSdkSession(),
    queryFn: () => getOmStore().products(),
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/** Start a checkout (popup; redirect when blocked). Re-reads holdings whatever the outcome. */
export function usePurchase(): (code: string) => Promise<PurchaseResult> {
  const qc = useQueryClient();
  return async (code) => {
    try {
      return await getOmStore().purchase(code);
    } finally {
      refreshStore(qc);
    }
  };
}

function refreshStore(qc: QueryClient): void {
  getOmStore().invalidate();
  void qc.invalidateQueries({ queryKey: ["store"] });
}

/**
 * On page load: when the URL carries `?om_order=` (back from a redirect-mode checkout), strip it
 * and re-read holdings. Inside a checkout popup the SDK also tells the opener and closes itself.
 * No network call when there's no `om_order`.
 */
export async function handleStoreReturn(qc: QueryClient): Promise<void> {
  if (!new URLSearchParams(window.location.search).has("om_order")) return;
  try {
    const order = await getOmStore().handleReturn();
    if (order) refreshStore(qc);
  } catch {
    // A failed read leaves holdings to the next normal load.
  }
}
